<template>
  <div class="home-frist-view" :class="{ active: creditList.length > 0 }">
    <div class="home-subscribe-card" :class="{ active: isExpanded }">
      <div class="hsc-head">
        <div class="left">
          <p>{{ t('current_my_subscription') }}</p>
          <span class="badge">{{ subscriptionStore.userPlan }}</span>
        </div>
        <button type="button" class="ep-col-btn" @click="toggleCard">
          <img :src="getAssetImageUrl('common/icons/ep-col-arrow.svg')" alt="" :class="{ rotated: isExpanded }" />
        </button>
      </div>

      <div ref="accordionBodyRef" class="expand-wrapper" :style="bodyStyle">
        <div v-if="subscriptionStore.currentPlan !== 'ENTERPRISE'" class="price-info">
          <p>{{ currentBillingCycle }}</p>
          <strong class="price">
            {{ monthlyPrice }} <span class="unit">/ {{ t('label_month') }}</span>
          </strong>
        </div>
        <div class="benefits-area">
          <div class="benefits-area">
            <p>{{ t('self_service_type_plan_feature', { plan: subscriptionStore.userPlan }) }}</p>
            <ul class="benefit-list">
              <li v-for="benefit in subscriptionStore.currentPlanBenefits" :key="benefit">
                <IconCheck class="icon white" />
                <span>{{ benefit }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div class="home-credit-card pd-all-5" :class="{ active: !isExpanded }">
      <div class="top-wrap">
        <div class="credit-info">
          <p>{{ t('total_credits') }}</p>
          <strong class="have-credit">{{ formatNumberWithComma(subscriptionStore.currentCredits) }}</strong>
        </div>
        <button type="button" class="gray-btn temporary-btn" @click="goToCreditList">
          <span>{{ t('credit_usage_history') }}</span>
        </button>
      </div>

      <div v-if="creditList.length > 0" class="expand-wrapper">
        <div class="credit-slide">
          <swiper
            :modules="[Pagination]"
            :slides-per-view="1"
            :space-between="20"
            :pagination="{ clickable: true }"
            class="my-swiper"
          >
            <swiper-slide v-for="item in creditList" :key="item.eventedAt" class="credit-history">
              <div class="left-col">
                <div :class="['icon-box', `${item.chatType}-icon`]">
                  <img :src="getAssetImageUrl(`common/icons/${item.chatType}-icon.svg`)" alt="chat-icon" />
                </div>

                <div class="text-info">
                  <span class="type gray-txt">{{ t(`${item.displayType}`) }}</span>
                  <span class="line"></span>
                  <span class="date gray-txt">{{ item.displayTime }}</span>
                </div>
              </div>

              <div class="right-col">
                <strong class="amount">
                  {{ item.creditSign }}{{ formatNumberWithComma(item.credit) }} {{ t('credit') }}
                </strong>
              </div>
            </swiper-slide>
          </swiper>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';
import 'swiper/css/pagination';

import { fetchSelfServiceCreditDetail } from '@/api/apiService';
import IconCheck from '@/components/jh-app/icons/IconCheck.vue';
import { useSubscriptionStore } from '@/stores/subscription';
import { CreditItem } from '@/types/components';
import { formatSimpleTimeAgo } from '@/utils/dateFormatter.js';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';
import { formatNumberWithComma } from '@/utils/stringUtils.js';

const { t } = useI18n();
const router = useRouter();
const subscriptionStore = useSubscriptionStore();

const creditList = ref<CreditItem[]>([]);

const isExpanded = ref<boolean>(false);
const accordionBodyRef = ref<HTMLElement | null>(null); // DOM에 접근하기 위한 ref
const currentHeight = ref('0px'); // 높이 제어 (초기값 0)
const displayStyle = ref('none'); // display 제어 (초기값 안보임)
const currentOpacity = ref(isExpanded.value ? '1' : '0');

const currentBillingCycle = computed<string>(() => {
  return subscriptionStore.currentPlanDetails?.billingCycle === 'MONTHLY'
    ? t('monthly_subscription_active')
    : t('annual_subscription_active');
});
const monthlyPrice = computed<string>(() => {
  if (subscriptionStore.currentPlan === 'FREE') {
    return `${t('ss_price_free')}`;
  }

  const currency = subscriptionStore.currency;
  const price = formatNumberWithComma(
    subscriptionStore.planPrices[subscriptionStore.currentPlan]?.[currency]?.pricePerMonth
  );

  return currency === 'KRW' ? `${price}${t('label_currency_won')}` : `$${price}`;
});

// --- 아코디언 스타일 (인라인 스타일 바인딩) ---
const bodyStyle = computed(() => ({
  height: currentHeight.value,
  display: displayStyle.value,
  opacity: currentOpacity.value,
  overflow: 'hidden',
  transition: 'height 0.3s ease, opacity 0.3s ease', // 부드러운 가속도 적용
}));

// --- 토글 함수 (핵심 로직 수정) ---
const toggleCard = async () => {
  const el = accordionBodyRef.value;
  if (!el) return;

  if (!isExpanded.value) {
    // 1. 열기 (Open)
    isExpanded.value = true;
    displayStyle.value = 'block'; // 먼저 보이게 처리

    // DOM 렌더링 대기 후 실제 높이 계산
    await nextTick();
    currentHeight.value = `${el.scrollHeight}px`;
    currentOpacity.value = '1';
  } else {
    // 2. 닫기 (Close)
    // 현재 높이를 픽셀로 명시 (auto -> pixel)하여 애니메이션 시작점 잡기
    currentHeight.value = `${el.scrollHeight}px`;
    currentOpacity.value = '1';

    // 브라우저가 높이 변화를 감지할 수 있도록 프레임 대기
    requestAnimationFrame(() => {
      currentHeight.value = '0px';
      currentOpacity.value = '0';
    });

    isExpanded.value = false;

    // 애니메이션 시간(0.3s) 후 display: none 처리 (레이아웃 잡힘 방지)
    setTimeout(() => {
      if (!isExpanded.value) {
        displayStyle.value = 'none';
      }
    }, 300);
  }
};

const fetchRecentCreditList = async () => {
  try {
    const { data } = await fetchSelfServiceCreditDetail(null, null);

    if (!data || data.datas.length === 0) {
      creditList.value = [];
      return;
    }

    creditList.value = data.datas.slice(0, 5).map((item) => {
      const [time, type] = formatSimpleTimeAgo(item.eventedAt);

      return {
        ...item,
        chatType: item.type === 'CHARGE' ? 'credit' : item.chatType.toLowerCase(),
        creditSign: item.type === 'USAGE' ? '-' : '+',
        displayType: item.type === 'CHARGE' ? 'ss_invoice_type_topup' : `label_${item.chatType.toLowerCase()}`,
        displayTime: `${time}${t(type)} ${t('label_before')}`,
      };
    });
  } catch (error) {
    console.error('최근 크레딧 사용 내역 조회 실패 :', error);
    creditList.value = [];
  }
};

const goToCreditList = () => {
  router.push({ name: 'app-credit' });
};

onMounted(async () => {
  await fetchRecentCreditList();
});
</script>

<style scoped lang="scss">
.home-frist-view {
  position: relative;
  min-height: 130px;
  &.active {
    min-height: 226px;
  }
}
.home-subscribe-card {
  &.active {
    // padding: 0 1.25rem 4.5rem;
    .benefits-area {
      padding: 0 0 2rem;
    }
  }
  background: $color-navy-100;
  border-radius: $size-m;
  color: #fff;
  padding: 0 1.25rem 2.5rem;

  .expand-wrapper {
    overflow: hidden;
    transition: all 0.4s ease-in-out;
  }
  .hsc-head {
    display: flex;
    justify-content: space-between;
    padding: 1rem 0;
    .left {
      display: flex;
      align-items: center;
      gap: $size-xs;
      .badge {
        color: #fff;
      }
    }
    .ep-col-btn {
      padding: 3px 6px;
      background: $color-transparent-white20;
      border-radius: $size-xs-m;
      img {
        transition: all 0.3s;
      }
    }
  }
  .price-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 1rem;
    p {
      font-weight: 600;
    }
    .price {
      font-size: $font-size-l;
      font-weight: 600;
      .unit {
        font-size: $font-size-xs;
        color: $color-transparent-white50;
      }
    }
  }
  .benefits-area {
    padding: 0 0 1rem;
    .benefit-list {
      padding-top: $size-m;
      li {
        display: flex;
        align-items: center;
        gap: $size-xs;
        margin-bottom: $size-s;
        span {
          color: #fff;
          font-weight: 300;
        }
      }
    }
  }
  p {
    color: #fff;
    font-size: $font-size-p;
  }
  .badge {
    background: $color-transparent-white20;
  }
}

.home-credit-card {
  &.active {
    .top-wrap {
      .credit-info {
        display: block;
        width: auto;
      }
    }
    .expand-wrapper {
      max-height: 200px;
    }
  }
  overflow: hidden;
  // margin-top: -30px;
  position: absolute;
  bottom: 0;
  left: 0;
  transition: all 0.3s ease-in-out;
  width: 100%;
  background: #fff;
  border-radius: $size-m;

  .top-wrap {
    padding-bottom: $size-xs;
    display: flex;
    justify-content: space-between;
    align-items: center;
    .credit-info {
      width: 100%;
      transition: all 0.3s;
      p {
        font-size: $font-size-xs;
        margin-bottom: $size-xs;
      }
      .have-credit {
        font-size: 1.5rem;
        font-weight: 700;
      }
    }
    .temporary-btn {
      border-radius: 100px;
      font-size: $font-size-psmall;
      padding: 0.5rem 1.25rem;
      height: 2.5rem;
      min-width: 88px;
      white-space: nowrap;
    }
  }
  .expand-wrapper {
    max-height: 0;
    overflow: hidden;
    transition: all 0.3s ease-in-out;
  }
}

.credit-slide {
  border-top: 1px solid $gray10;
  margin-top: 1rem;
  padding: 0.5rem 0 0;

  .credit-history {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: $size-s 0;
    .left-col {
      display: flex;
      align-items: center;
      gap: $size-s;
      .icon-box {
        width: 2rem;
        height: 2rem;
        border-radius: $size-m;
        display: flex;
        align-items: center;
        justify-content: center;
        &.meeting-icon {
          background: $meeting-bg;
        }
        &.conference-icon {
          background: $conference-bg;
        }
        &.audience-icon {
          background: $audience-bg;
        }
        &.general-icon {
          background: $general-bg;
        }
        &.single-icon {
          background: $single-bg;
        }
        &.credit-icon {
          background: $credit-bg;
        }
        img {
          width: 1.125rem;
          height: 1.125rem;
        }
      }
      .text-info {
        font-size: $font-size-xs;
        display: flex;
        align-items: center;
        gap: 4px;
        .line {
          width: 1px;
          height: 8px;
          background: $color-gray-50;
        }
      }
    }
    .right-col {
      .amount {
        font-weight: 600;
      }
    }
  }
}
:deep(.swiper-pagination) {
  position: relative;
  /* 위치 조정이나 다른 스타일도 여기서 */
  bottom: 0;
  top: 0;
}
.icon.white {
  color: #fff;
}

.rotated {
  transform: rotate(-180deg);
}
</style>
